/**
 * Kiosk persistence (app-kiosk brief → Storage).
 *
 * Web: everything lives in ONE IndexedDB database on this origin; nothing goes to localStorage. The credential, the business
 * key set and the roster are the sensitive parts (packages/checkin README → "Why the kiosk holds the business key"), so one
 * `wipe()` removes all of it and runs on revocation.
 * Native: the credential goes to secure storage through the bridge when it offers one. **Assumed** shape,
 * `HelmrNative.secureStorage`, until app-native lands; the rest stays in IndexedDB inside the WebView.
 */
import type { CheckinEvent, KeySet, Plan } from "@helmr/checkin";
import type { KioskConfig, KioskMode } from "../../api/kiosk";

/** What pairing hands back, plus what the kiosk needs to show before the first config call. */
export interface KioskCredential {
  /** `<businessId>.<kioskId>.<secret>`. Sent to `/kiosk/*` only. */
  token: string;
  kioskId: string;
  businessId: string;
  branchId: string;
  label: string;
  mode: KioskMode;
  /** The business name, shown large in the chrome. The config call carries only the branch. */
  gymName: string;
  pairedAt: number;
}

export interface RosterEntry { name: string; photoUrl: string | null; plans: Plan[]; balanceDue: boolean }
export interface RosterCache { members: Record<string, RosterEntry>; asOf: number; fullAt: number }
export interface WallSecretCache { kid: string; kioskSecret: string; notAfter: number }

/** Every key the kiosk stores, with its value type. */
export interface KioskRecords {
  credential: KioskCredential;
  config: KioskConfig;
  keyset: KeySet;
  roster: RosterCache;
  wall: WallSecretCache;
  /** `OfflineCheckinQueue.snapshot()`: everything not yet acknowledged, including batches in flight. */
  queue: CheckinEvent[];
}
export type RecordKey = keyof KioskRecords;

export interface KioskStore {
  read<K extends RecordKey>(key: K): Promise<KioskRecords[K] | null>;
  write<K extends RecordKey>(key: K, value: KioskRecords[K]): Promise<void>;
  /** Removes the credential, keys, roster, queue and cached config. */
  wipe(): Promise<void>;
}

const ALL_KEYS: RecordKey[] = ["credential", "config", "keyset", "roster", "wall", "queue"];

export function memoryKioskStore(initial: Partial<KioskRecords> = {}): KioskStore & { dump(): Partial<KioskRecords> } {
  let data: Partial<KioskRecords> = structuredClone(initial);
  return {
    async read(key) { return (structuredClone(data[key]) ?? null) as never; },
    async write(key, value) { data = { ...data, [key]: structuredClone(value) }; },
    async wipe() { data = {}; },
    dump: () => data,
  };
}

/** Assumed native bridge surface (app-native). All three calls are no-ops on the web. */
export interface SecureStorageBridge {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}
export const nativeSecureStorage = (): SecureStorageBridge | null =>
  (globalThis as { HelmrNative?: { secureStorage?: SecureStorageBridge } }).HelmrNative?.secureStorage ?? null;

export const KIOSK_DB = "helmr-kiosk";
const KV = "kv";

function openDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(KIOSK_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(KV);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB is not available"));
    req.onblocked = () => reject(new Error("IndexedDB is blocked"));
  });
}

const done = <T,>(req: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
const txDone = (tx: IDBTransaction): Promise<void> => new Promise((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error); });

export function indexedDbKioskStore(opts: { factory?: IDBFactory; secure?: SecureStorageBridge | null } = {}): KioskStore {
  const factory = opts.factory ?? globalThis.indexedDB;
  const secure = opts.secure === undefined ? nativeSecureStorage() : opts.secure;
  let db: Promise<IDBDatabase> | null = null;
  const handle = () => (db ??= openDb(factory));
  const SECURE_KEY = "helmr.kiosk.credential";
  return {
    async read(key) {
      if (key === "credential" && secure) {
        const raw = await secure.get(SECURE_KEY);
        return (raw ? JSON.parse(raw) : null) as never;
      }
      const d = await handle();
      const v = await done(d.transaction(KV, "readonly").objectStore(KV).get(key));
      return (v ?? null) as never;
    },
    async write(key, value) {
      if (key === "credential" && secure) return secure.set(SECURE_KEY, JSON.stringify(value));
      const d = await handle();
      const tx = d.transaction(KV, "readwrite");
      tx.objectStore(KV).put(value, key);
      await txDone(tx);
    },
    async wipe() {
      if (secure) await secure.remove(SECURE_KEY);
      const d = await handle();
      const tx = d.transaction(KV, "readwrite");
      for (const k of ALL_KEYS) tx.objectStore(KV).delete(k);
      await txDone(tx);
    },
  };
}
